import cn from "classnames";
import { If } from "components";
import { t } from "i18next";
import { useMemo } from "react";
import { InfoTooltipIcon } from "ui";

import { ResellerPromoCodeStatus } from "../../constants";
import { ResellerPromoCode } from "../../resellers.types";
import { ResellerPromoCodesProps } from "./ResellerPromoCodes.types";

import styles from "./ResellerPromoCodes.module.scss";

const PromoCode = ({ promoCode }: { promoCode: ResellerPromoCode }) => (
  <span
    className={cn(styles.code, {
      [styles.code__permanent]: promoCode.isPermanent,
      [styles.code__inactive]: promoCode.status !== ResellerPromoCodeStatus.Active
    })}
  >
    <span className={styles.code_value}>{promoCode.code}</span>

    <span className={styles.code_discount}>
      {promoCode.discountPercentage
        ? `-${promoCode.discountPercentage}%`
        : `-${promoCode.discountAmount.toLocaleString("en-US")} FCFA`}
    </span>

    <span className={styles.code_usage}>
      {`${promoCode.timesUsed} ${t("uses")}`}
    </span>
  </span>
);

/**
 * The influencer's codes, split the way the commission rules split them: the
 * permanent code is the one that ties a first-time customer to the influencer
 * for life, campaign codes only pay on the order they are typed on.
 */
const ResellerPromoCodes = ({
  promoCodes,
  className
}: ResellerPromoCodesProps) => {
  const { permanentCode, campaignCodes } = useMemo(
    () => ({
      permanentCode: promoCodes?.find((promoCode) => promoCode.isPermanent),
      campaignCodes:
        promoCodes?.filter((promoCode) => !promoCode.isPermanent) ?? []
    }),
    [promoCodes]
  );

  if (!promoCodes) return null;

  return (
    <div className={cn(styles.promoCodes, className)}>
      <div className={styles.promoCodes_block}>
        <span className={styles.promoCodes_label}>
          {t("Permanent code")}
          <InfoTooltipIcon
            id="permanent-promo-code-tooltip"
            iconSize={14}
            tooltipText={t(
              "A first-time customer who uses this code is linked to the influencer for life: every later order they place earns a commission, with no code involved."
            )}
          />
        </span>

        {permanentCode ? (
          <div className={styles.promoCodes_codes}>
            <PromoCode promoCode={permanentCode} />
          </div>
        ) : (
          <span className={styles.promoCodes_empty}>
            {t("No permanent code yet")}
          </span>
        )}
      </div>

      <If condition={campaignCodes.length > 0}>
        <div className={styles.promoCodes_block}>
          <span className={styles.promoCodes_label}>
            {t("Limited-time codes")}
            <InfoTooltipIcon
              id="campaign-promo-codes-tooltip"
              iconSize={14}
              tooltipText={t(
                "A limited-time code discounts the order and pays a commission on it, but links nobody to the influencer. It expires; the permanent code does not."
              )}
            />
          </span>

          <div className={styles.promoCodes_codes}>
            {campaignCodes.map((promoCode) => (
              <PromoCode key={promoCode.code} promoCode={promoCode} />
            ))}
          </div>
        </div>
      </If>
    </div>
  );
};

export default ResellerPromoCodes;
